import { useEffect, useState } from "react";
import { Users, Copy, Check, Crown, Sparkles, Hourglass } from "lucide-react";
import { api } from "@/lib/api";
import { useLang } from "@/context/LangContext";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

export function CircleBoard({ dark = false, compact = false }) {
  const [d, setD] = useState(null);
  const [copied, setCopied] = useState(false);
  const { t } = useLang();

  useEffect(() => { api.get("/referral/circle").then(({ data }) => setD(data)).catch(() => setD(false)); }, []);
  if (!d) return null;

  const copy = () => {
    navigator.clipboard.writeText(`${window.location.origin}${d.share_path}`);
    setCopied(true); setTimeout(() => setCopied(false), 2000); toast.success(t("copied"));
  };
  const friends = compact ? d.friends.slice(0, 3) : d.friends;
  const muted = dark ? "text-white/60" : "text-muted-foreground";
  const card = dark ? "border-white/15 bg-white/5" : "border-border bg-card";

  return (
    <div data-testid="circle-board" className={cn("rounded-3xl border p-5", card, dark ? "text-white" : "")}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Users className={cn("h-5 w-5", dark ? "text-amber-300" : "text-indigo-600")} />
          <h3 className="font-display text-lg font-bold">{t("circleTitle")}</h3>
        </div>
        <span className={cn("text-xs font-bold", muted)} data-testid="circle-stats">
          {t("circleStats", { n: d.stats.invited, c: d.stats.completed_dna })}
        </span>
      </div>

      {d.me.compatibility != null && d.stats.completed_dna > 0 && (
        <p className={cn("mt-2 text-sm", muted)} data-testid="circle-rank">
          {t("circleRank", { r: d.my_rank, total: d.stats.completed_dna + 1 })}
          {d.stats.same_archetype > 0 && <> · {t("circleSame", { n: d.stats.same_archetype })}</>}
        </p>
      )}

      {d.friends.length === 0 ? (
        <div className="mt-4 text-center" data-testid="circle-empty">
          <p className={cn("text-sm", muted)}>{t("circleEmpty")}</p>
        </div>
      ) : (
        <ol className="mt-4 space-y-2" data-testid="circle-list">
          {friends.map((f, i) => (
            <li key={f.id} data-testid={`circle-friend-${f.id}`}
              className={cn("flex items-center gap-3 rounded-2xl border px-3 py-2.5", dark ? "border-white/10 bg-white/5" : "border-border bg-secondary/50")}>
              <span className={cn("flex h-7 w-7 shrink-0 items-center justify-center rounded-full font-mono text-xs font-bold",
                f.completed ? (i === 0 ? "bg-amber-400 text-slate-900" : (dark ? "bg-white/15" : "bg-slate-900 text-white")) : (dark ? "bg-white/10" : "bg-secondary"))}>
                {f.completed ? (i === 0 ? <Crown className="h-3.5 w-3.5" /> : i + 1) : <Hourglass className="h-3.5 w-3.5 opacity-60" />}
              </span>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-1.5">
                  <span className="truncate text-sm font-bold">{f.name}</span>
                  {f.same_archetype && (
                    <span data-testid="same-archetype-badge" className="inline-flex items-center gap-1 rounded-full bg-emerald-500/20 px-2 py-0.5 text-[10px] font-bold text-emerald-500">
                      <Sparkles className="h-3 w-3" /> {t("circleSameBadge")}
                    </span>
                  )}
                </div>
                <div className={cn("truncate text-xs", muted)}>
                  {f.completed ? <>{f.archetype} · {f.best_match}</> : t("circleDecoding")}
                </div>
              </div>
              {f.completed && (
                <span className={cn("font-display text-lg font-extrabold", dark ? "text-amber-300" : "text-indigo-600")}>{f.compatibility}%</span>
              )}
            </li>
          ))}
        </ol>
      )}

      <button onClick={copy} data-testid="circle-invite-btn"
        className={cn("mt-4 flex w-full items-center justify-center gap-2 rounded-full py-3 text-sm font-bold transition-colors",
          dark ? "bg-white text-slate-900 hover:bg-white/90" : "bg-slate-900 text-white hover:bg-indigo-600")}>
        {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />} {d.friends.length === 0 ? t("circleInvite") : t("circleInviteMore")}
      </button>
    </div>
  );
}
